import React, { useEffect, useState } from 'react';
import { useOutlet } from '../lib/OutletContext';
import { 
  ShoppingCart, 
  AlertTriangle, 
  TrendingUp, 
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Package,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  Info,
  FileText,
  Check,
  X,
  Building2,
  Loader2,
  Calendar,
  Plus,
  Trash2,
  Zap,
  Target,
  BarChart3
} from 'lucide-react';
import { formatNumber, formatCurrency } from '../lib/utils';
import { toast } from 'sonner';
import api, { 
  generatePurchaseOrders, 
  getPurchaseOrders, 
  approvePurchaseOrder, 
  cancelPurchaseOrder,
  getDemandOverrides,
  createDemandOverride,
  deleteDemandOverride,
  getForecastAccuracy
} from '../lib/api';
import { cn } from '../lib/utils';
import logger from '../lib/logger';
import { useReconciliationBadge } from '../lib/ReconciliationBadgeContext';
import { Link } from 'react-router-dom';

const Procurement = () => {
  const { selectedOutlet } = useOutlet();
  const { pendingCount: reconciliationPending } = useReconciliationBadge();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [plan, setPlan] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});
  
  // PO state
  const [view, setView] = useState('plan'); // 'plan' | 'orders' | 'overrides' | 'accuracy'
  const [generatingPO, setGeneratingPO] = useState(false);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loadingPOs, setLoadingPOs] = useState(false);
  const [approvingPO, setApprovingPO] = useState(null);
  const [expandedPOs, setExpandedPOs] = useState({});
  
  // Demand Overrides state
  const [overrides, setOverrides] = useState([]);
  const [loadingOverrides, setLoadingOverrides] = useState(false);
  const [showCreateOverride, setShowCreateOverride] = useState(false);
  const [newOverride, setNewOverride] = useState({
    name: '',
    start_date: '',
    end_date: '',
    demand_multiplier: 1.5,
    notes: ''
  });
  
  // Forecast Accuracy state
  const [accuracy, setAccuracy] = useState(null);
  const [loadingAccuracy, setLoadingAccuracy] = useState(false);
  const [accuracyPeriod, setAccuracyPeriod] = useState(7);

  useEffect(() => {
    if (selectedOutlet) {
      loadPlan();
      loadPurchaseOrders();
      loadOverrides();
      loadAccuracy();
    }
  }, [selectedOutlet]);
  
  useEffect(() => {
    if (selectedOutlet && view === 'accuracy') {
      loadAccuracy();
    }
  }, [accuracyPeriod]);

  const loadPlan = async () => {
    if (!selectedOutlet) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/inventory/procurement/plan?outlet_id=${selectedOutlet}`);
      setPlan(response.data);
    } catch (error) {
      logger.error('Failed to load procurement plan:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPurchaseOrders = async () => {
    if (!selectedOutlet) return;
    
    setLoadingPOs(true);
    try {
      const response = await getPurchaseOrders(selectedOutlet);
      setPurchaseOrders(response.purchase_orders || []);
    } catch (error) {
      logger.error('Failed to load purchase orders:', error);
    } finally {
      setLoadingPOs(false);
    }
  };

  const loadOverrides = async () => {
    if (!selectedOutlet) return;
    
    setLoadingOverrides(true);
    try {
      const response = await getDemandOverrides(selectedOutlet);
      setOverrides(response.overrides || []);
    } catch (error) {
      logger.error('Failed to load demand overrides:', error);
    } finally {
      setLoadingOverrides(false);
    }
  };
  
  const loadAccuracy = async () => {
    if (!selectedOutlet) return;
    
    setLoadingAccuracy(true);
    try {
      const response = await getForecastAccuracy(selectedOutlet, accuracyPeriod);
      setAccuracy(response);
    } catch (error) {
      logger.error('Failed to load forecast accuracy:', error);
    } finally {
      setLoadingAccuracy(false);
    }
  };

  const handleCreateOverride = async (e) => {
    e.preventDefault();
    
    if (!newOverride.name || !newOverride.start_date || !newOverride.end_date) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      await createDemandOverride({
        outlet_id: selectedOutlet,
        ...newOverride
      });
      toast.success('Demand override created');
      setShowCreateOverride(false);
      setNewOverride({
        name: '',
        start_date: '',
        end_date: '',
        demand_multiplier: 1.5,
        notes: ''
      });
      await loadOverrides();
      await loadPlan(); // Refresh plan to show override effect
    } catch (error) {
      toast.error('Failed to create override');
      logger.error(error);
    }
  };

  const handleDeleteOverride = async (overrideId) => {
    try {
      await deleteDemandOverride(overrideId);
      toast.success('Override deleted');
      await loadOverrides();
      await loadPlan();
    } catch (error) {
      toast.error('Failed to delete override');
      logger.error(error);
    }
  };

  const regeneratePlan = async () => {
    setGenerating(true);
    try {
      const response = await api.get(`/inventory/procurement/plan?outlet_id=${selectedOutlet}`);
      setPlan(response.data);
      toast.success('Procurement plan regenerated');
    } catch (error) {
      toast.error('Failed to regenerate plan');
      logger.error(error);
    } finally {
      setGenerating(false);
    }
  };

  const handleGeneratePO = async () => {
    if (!plan || !plan.items || plan.items.length === 0) {
      toast.error('No items in plan to generate PO');
      return;
    }

    setGeneratingPO(true);
    try {
      const response = await generatePurchaseOrders(selectedOutlet, plan.items);
      toast.success(`Generated ${response.po_count} purchase order(s)`);
      await loadPurchaseOrders();
      setView('orders');
    } catch (error) {
      toast.error('Failed to generate purchase orders');
      logger.error(error);
    } finally {
      setGeneratingPO(false);
    }
  };

  const handleApprovePO = async (poId) => {
    setApprovingPO(poId);
    try {
      await approvePurchaseOrder(poId);
      toast.success('Purchase order approved');
      await loadPurchaseOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve PO');
      logger.error(error);
    } finally {
      setApprovingPO(null);
    }
  };

  const handleCancelPO = async (poId) => {
    try {
      await cancelPurchaseOrder(poId);
      toast.success('Purchase order cancelled');
      await loadPurchaseOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel PO');
      logger.error(error);
    }
  };

  const toggleExpand = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const toggleExpandPO = (poId) => {
    setExpandedPOs(prev => ({
      ...prev,
      [poId]: !prev[poId]
    }));
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'HIGH':
        return 'bg-red-500/10 text-red-500 border-red-500/30';
      case 'MEDIUM':
        return 'bg-amber-500/10 text-amber-500 border-amber-500/30';
      case 'LOW':
        return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30';
      default:
        return 'bg-zinc-500/10 text-zinc-500 border-zinc-500/30';
    }
  };

  const getConfidenceColor = (band) => {
    switch (band) {
      case 'HIGH':
        return 'text-emerald-500';
      case 'MEDIUM':
        return 'text-amber-500';
      case 'LOW':
        return 'text-orange-500';
      case 'VERY_LOW':
        return 'text-red-500';
      default:
        return 'text-zinc-500';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'DRAFT':
        return 'bg-blue-500/10 text-blue-500 border-blue-500/30';
      case 'APPROVED':
        return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30';
      case 'CANCELLED':
        return 'bg-zinc-500/10 text-zinc-500 border-zinc-500/30';
      default:
        return 'bg-zinc-500/10 text-zinc-500 border-zinc-500/30';
    }
  };

  const getReasonLabel = (code) => {
    const labels = {
      'LOW_STOCK': 'Low Stock',
      'FAST_MOVING': 'Fast Moving',
      'WEEKEND_SPIKE': 'Weekend Spike',
      'WEEKDAY_PATTERN': 'Weekday Pattern',
      'TRENDING_UP': 'Trending Up',
      'LOCAL_OVERRIDE_ACTIVE': 'Event Override',
      'PRICE_RISING': 'Price Rising',
      'TRANSFER_NOT_AVAILABLE': 'Transfer N/A',
      'PREFERRED_VENDOR_SELECTED': 'Preferred Vendor',
      'FALLBACK_PRICING_USED': 'Est. Price',
      'SALES_DEMAND_SIGNAL': 'Sales Signal',
      'SALES_HIGH_CONFIDENCE': 'Sales: High Conf.',
      'SALES_MEDIUM_CONFIDENCE': 'Sales: Med Conf.',
      'SALES_LOW_CONFIDENCE': 'Sales: Low Conf.',
      'SALES_HIGH_VARIANCE': 'Sales: Volatile',
      'SALES_LOW_DATA': 'Sales: Low Data',
      'SALES_NEW_ITEM': 'Sales: New Item',
      'UNMAPPED_SALES_ITEM': 'Unmapped Item'
    };
    return labels[code] || code;
  };

  const getVendorBasisLabel = (basis) => {
    const labels = {
      'LOWEST_RECENT_PRICE': 'Best Price',
      'PREFERRED_VENDOR': 'Preferred',
      'LAST_USED_VENDOR': 'Last Used',
      'FALLBACK_AVG_COST': 'Avg. Cost',
      'MANUAL_SELECTION_REQUIRED': 'Select Vendor'
    };
    return labels[basis] || basis;
  };

  const draftPOs = purchaseOrders.filter(po => po.status === 'DRAFT');
  const approvedPOs = purchaseOrders.filter(po => po.status === 'APPROVED');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="procurement-page">
      {/* Reconciliation banner — non-blocking, shown only when pending items exist */}
      {reconciliationPending > 0 && (
        <div
          className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 flex items-center gap-3"
          data-testid="procurement-reconciliation-banner"
        >
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
          <p className="text-sm text-amber-200 flex-1">
            You have <strong>{reconciliationPending}</strong> pending reconciliation
            {reconciliationPending === 1 ? ' item' : ' items'}. Procurement recommendations
            may change after resolution.
          </p>
          <Link
            to="/reconciliation"
            className="px-3 py-1.5 rounded-md bg-amber-500/10 hover:bg-amber-500/20 text-amber-200 text-xs font-medium border border-amber-500/30 whitespace-nowrap"
            data-testid="procurement-reconciliation-banner-link"
          >
            Review queue
          </Link>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight mb-2" data-testid="procurement-title">
            What Should I Buy Today?
          </h1>
          <p className="text-zinc-400">
            AI-powered procurement recommendations
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <div className="flex bg-[#18181b] border border-[#27272a] rounded-lg p-1">
            <button
              onClick={() => setView('plan')}
              className={cn(
                "px-4 py-2 rounded-md text-sm font-medium transition-colors",
                view === 'plan' 
                  ? "bg-emerald-500 text-white" 
                  : "text-zinc-400 hover:text-white"
              )}
              data-testid="view-plan-btn"
            >
              <Package className="w-4 h-4 inline mr-2" />
              Plan
            </button>
            <button
              onClick={() => setView('orders')}
              className={cn(
                "px-4 py-2 rounded-md text-sm font-medium transition-colors relative",
                view === 'orders' 
                  ? "bg-emerald-500 text-white" 
                  : "text-zinc-400 hover:text-white"
              )}
              data-testid="view-orders-btn"
            >
              <FileText className="w-4 h-4 inline mr-2" />
              Orders
              {draftPOs.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center">
                  {draftPOs.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setView('overrides')}
              className={cn(
                "px-4 py-2 rounded-md text-sm font-medium transition-colors relative",
                view === 'overrides' 
                  ? "bg-emerald-500 text-white" 
                  : "text-zinc-400 hover:text-white"
              )}
              data-testid="view-signals-btn"
            >
              <Zap className="w-4 h-4 inline mr-2" />
              Signals
              {overrides.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-violet-500 rounded-full text-xs flex items-center justify-center">
                  {overrides.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setView('accuracy')}
              className={cn(
                "px-4 py-2 rounded-md text-sm font-medium transition-colors",
                view === 'accuracy' 
                  ? "bg-emerald-500 text-white" 
                  : "text-zinc-400 hover:text-white"
              )}
              data-testid="view-accuracy-btn"
            >
              <Target className="w-4 h-4 inline mr-2" />
              Accuracy
            </button>
          </div>

          {view === 'plan' && (
            <>
              <button
                onClick={regeneratePlan}
                disabled={generating}
                className="flex items-center gap-2 bg-zinc-700 hover:bg-zinc-600 text-white font-bold py-2 px-4 rounded-lg transition-colors disabled:opacity-50"
                data-testid="regenerate-plan-button"
              >
                <RefreshCw className={cn("w-5 h-5", generating && "animate-spin")} />
                Refresh
              </button>
              <button
                onClick={handleGeneratePO}
                disabled={generatingPO || !plan || plan.items.length === 0}
                className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-2 px-4 rounded-lg transition-colors disabled:opacity-50"
                data-testid="generate-po-button"
              >
                {generatingPO ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <ShoppingCart className="w-5 h-5" />
                )}
                {generatingPO ? 'Generating...' : 'Generate PO'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Plan View */}
      {view === 'plan' && (
        <>
          {/* Sales demand fetch error — top-level, never silently hide failure */}
          {plan?.diagnostics?.sales_demand_fetch_error && (
            <div
              className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 flex items-center gap-3"
              data-testid="sales-demand-fetch-error-banner"
            >
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-red-200 font-medium">
                  Sales demand data unavailable. Using historical consumption only.
                </p>
                <p className="text-xs text-red-300/70 mt-0.5">
                  {plan.diagnostics.sales_demand_fetch_error}
                </p>
              </div>
              <button
                onClick={loadPlan}
                className="px-3 py-1.5 rounded-md bg-red-500/10 hover:bg-red-500/20 text-red-200 text-xs font-medium border border-red-500/30 whitespace-nowrap"
                data-testid="sales-demand-fetch-error-retry"
              >
                Retry
              </button>
            </div>
          )}

          {/* Unmapped sales items callout — actionable, sends operator to Mapping */}
          {plan?.diagnostics?.sales_demand_items_unmapped > 0 && (
            <div
              className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 flex items-center gap-3"
              data-testid="unmapped-sales-items-callout"
            >
              <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-amber-200">
                  <strong data-testid="unmapped-sales-items-count">
                    {plan.diagnostics.sales_demand_items_unmapped}
                  </strong>{' '}
                  sales {plan.diagnostics.sales_demand_items_unmapped === 1 ? 'item is' : 'items are'} not mapped to recipes — they're excluded from the demand forecast.
                </p>
                {plan.diagnostics.sales_demand_unmapped_item_ids?.length > 0 && (
                  <p className="text-xs text-amber-300/70 mt-0.5 font-mono">
                    {plan.diagnostics.sales_demand_unmapped_item_ids.slice(0, 5).join(', ')}
                    {plan.diagnostics.sales_demand_unmapped_item_ids.length > 5 && ` +${plan.diagnostics.sales_demand_unmapped_item_ids.length - 5} more`}
                  </p>
                )}
              </div>
              <Link
                to="/recipes/mapping"
                className="px-3 py-1.5 rounded-md bg-amber-500/10 hover:bg-amber-500/20 text-amber-200 text-xs font-medium border border-amber-500/30 whitespace-nowrap"
                data-testid="unmapped-sales-items-link"
              >
                Map items
              </Link>
            </div>
          )}

          {/* Summary Cards */}
          {plan && plan.summary && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="summary-cards">
              {/* Total Items */}
              <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <Package className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-xs text-zinc-400 uppercase tracking-wider">Items to Order</p>
                    <p className="text-2xl font-bold" data-testid="total-items">
                      {plan.summary.total_recommended_items}
                    </p>
                  </div>
                </div>
              </div>

              {/* High Urgency */}
              <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <p className="text-xs text-zinc-400 uppercase tracking-wider">High Urgency</p>
                    <p className="text-2xl font-bold text-red-500" data-testid="high-urgency">
                      {plan.summary.high_urgency_items}
                    </p>
                  </div>
                </div>
              </div>

              {/* Total Cost */}
              <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div>
                    <p className="text-xs text-zinc-400 uppercase tracking-wider">Est. Total Cost</p>
                    <p className="text-2xl font-bold" data-testid="total-cost">
                      {formatCurrency(plan.summary.total_estimated_cost)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Confidence */}
              <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-violet-500" />
                  </div>
                  <div>
                    <p className="text-xs text-zinc-400 uppercase tracking-wider">Confidence</p>
                    <p className={cn("text-2xl font-bold", getConfidenceColor(plan.summary.overall_confidence_band))} data-testid="confidence">
                      {(plan.summary.overall_confidence_score * 100).toFixed(0)}%
                      <span className="text-sm ml-1">({plan.summary.overall_confidence_band})</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Recommendations List */}
          <div className="space-y-3" data-testid="recommendations-list">
            {!plan || plan.items.length === 0 ? (
              <div className="text-center py-16 bg-[#18181b] border border-[#27272a] rounded-lg">
                <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">All Stocked Up!</h3>
                <p className="text-zinc-400 max-w-md mx-auto">
                  No procurement recommendations at this time. Your inventory levels are sufficient for the planning horizon.
                </p>
              </div>
            ) : (
              plan.items.map((item) => (
                <div
                  key={item.ingredient_id}
                  className="bg-[#18181b] border border-[#27272a] rounded-lg overflow-hidden"
                  data-testid="recommendation-card"
                >
                  {/* Main Row */}
                  <div 
                    className="p-4 cursor-pointer hover:bg-[#1f1f23] transition-colors"
                    onClick={() => toggleExpand(item.ingredient_id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        {/* Urgency Badge */}
                        <span className={cn(
                          "px-2 py-1 rounded text-xs font-bold border",
                          getUrgencyColor(item.urgency)
                        )}>
                          {item.urgency}
                        </span>

                        {/* Ingredient Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-lg" data-testid="item-name">{item.ingredient_name}</h3>
                          <div className="flex items-center gap-4 text-sm text-zinc-400">
                            <span>Stock: <span className="text-white font-mono">{formatNumber(item.current_stock)} {item.base_unit}</span></span>
                            <span>•</span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {item.days_to_stockout < 999 ? (
                                <span className={item.days_to_stockout < 1.5 ? "text-red-500" : ""}>
                                  {formatNumber(item.days_to_stockout)} days to stockout
                                </span>
                              ) : (
                                <span>No consumption data</span>
                              )}
                            </span>
                          </div>
                        </div>

                        {/* Order Recommendation */}
                        <div className="text-right">
                          <p className="text-xs text-zinc-400 uppercase">
                            {item.supply_option?.mode === 'TRANSFER' ? 'Recommended Transfer' : 'Recommended Order'}
                          </p>
                          <p className="text-2xl font-bold text-emerald-500" data-testid="recommended-qty">
                            {formatNumber(item.supply_option?.mode === 'TRANSFER' ? item.supply_option.transfer_quantity : item.rounded_order_qty)} {item.base_unit}
                          </p>
                          {item.supply_option?.mode === 'TRANSFER' ? (
                            <p className="text-xs text-blue-400">
                              from {item.supply_option.transfer_source_outlet_name}
                            </p>
                          ) : (
                            <p className="text-xs text-zinc-500">
                              Est. {formatCurrency(item.vendor_recommendation.estimated_line_cost)}
                            </p>
                          )}
                          {/* Change vs Yesterday */}
                          {item.change_vs_previous?.has_previous && Math.abs(item.change_vs_previous.delta_pct) >= 10 && (
                            <p className={cn(
                              "text-xs font-semibold mt-1",
                              item.change_vs_previous.delta_qty > 0 ? "text-amber-400" : "text-cyan-400"
                            )}>
                              {item.change_vs_previous.delta_qty > 0 ? '↑' : '↓'} {Math.abs(item.change_vs_previous.delta_qty).toFixed(1)} {item.base_unit} vs yesterday
                            </p>
                          )}
                        </div>

                        {/* Expand Button */}
                        <div className="ml-4">
                          {expandedItems[item.ingredient_id] ? (
                            <ChevronUp className="w-5 h-5 text-zinc-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-zinc-400" />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Reason Tags */}
                    <div className="flex flex-wrap gap-2 mt-3">
                      {/* Supply Mode Badge */}
                      {item.supply_option?.mode === 'TRANSFER' ? (
                        <span className="px-2 py-0.5 bg-blue-500/10 text-blue-400 border border-blue-500/30 rounded text-xs font-semibold">
                          TRANSFER
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 rounded text-xs font-semibold">
                          PURCHASE
                        </span>
                      )}
                      
                      {/* Vendor Selection Basis */}
                      {item.vendor_recommendation?.selection_basis && item.supply_option?.mode !== 'TRANSFER' && (
                        <span className={cn(
                          "px-2 py-0.5 rounded text-xs",
                          item.vendor_recommendation.selection_basis === 'LOWEST_RECENT_PRICE' && "bg-emerald-500/10 text-emerald-400",
                          item.vendor_recommendation.selection_basis === 'PREFERRED_VENDOR' && "bg-violet-500/10 text-violet-400",
                          item.vendor_recommendation.selection_basis === 'MANUAL_SELECTION_REQUIRED' && "bg-amber-500/10 text-amber-400",
                          item.vendor_recommendation.selection_basis === 'FALLBACK_AVG_COST' && "bg-zinc-500/10 text-zinc-400"
                        )}>
                          {getVendorBasisLabel(item.vendor_recommendation.selection_basis)}
                        </span>
                      )}
                      
                      {/* Vendor Name */}
                      {item.vendor_recommendation?.vendor_name && item.supply_option?.mode !== 'TRANSFER' && (
                        <span className="px-2 py-0.5 bg-zinc-800 text-zinc-300 rounded text-xs flex items-center gap-1">
                          <Building2 className="w-3 h-3" />
                          {item.vendor_recommendation.vendor_name}
                        </span>
                      )}
                      
                      {item.reason_codes.map((code, idx) => (
                        <span 
                          key={`rc-${code}-${idx}`}
                          className="px-2 py-0.5 bg-zinc-800 text-zinc-300 rounded text-xs"
                        >
                          {getReasonLabel(code)}
                        </span>
                      ))}
                      {/* Sales demand chip — visible only when blended */}
                      {item.demand_source === 'sales_capture' && (
                        <span
                          className="px-2 py-0.5 bg-cyan-500/10 text-cyan-300 border border-cyan-500/30 rounded text-xs flex items-center gap-1"
                          data-testid="sales-demand-chip"
                          title="Forecast incorporates sales-capture demand signal"
                        >
                          <Zap className="w-3 h-3" />
                          Sales demand signal
                        </span>
                      )}
                      <span className={cn(
                        "px-2 py-0.5 rounded text-xs flex items-center gap-1",
                        getConfidenceColor(item.confidence_band)
                      )}>
                        <Info className="w-3 h-3" />
                        {(item.confidence_score * 100).toFixed(0)}% confidence
                      </span>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedItems[item.ingredient_id] && (
                    <div className="border-t border-[#27272a] p-4 bg-[#09090b]">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Forecast Details */}
                        <div>
                          <h4 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Forecast</h4>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span className="text-zinc-400">7-day avg:</span>
                              <span className="font-mono">{formatNumber(item.avg_daily_consumption_7d)}/day</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-zinc-400">14-day avg:</span>
                              <span className="font-mono">{formatNumber(item.avg_daily_consumption_14d)}/day</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-zinc-400">DOW factor:</span>
                              <span className="font-mono">{item.day_of_week_factor.toFixed(2)}x</span>
                            </div>
                            {item.trend_factor > 1 && (
                              <div className="flex justify-between">
                                <span className="text-zinc-400">Trend:</span>
                                <span className="font-mono text-amber-500">+{((item.trend_factor - 1) * 100).toFixed(0)}%</span>
                              </div>
                            )}
                            {item.override_factor > 1 && (
                              <div className="flex justify-between">
                                <span className="text-zinc-400">Override:</span>
                                <span className="font-mono text-violet-500">{item.override_factor.toFixed(1)}x</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Stock Calculation */}
                        <div>
                          <h4 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Calculation</h4>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span className="text-zinc-400">Daily forecast:</span>
                              <span className="font-mono">{formatNumber(item.forecast_daily_consumption)} {item.base_unit}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-zinc-400">{item.forecast_horizon_days}-day total:</span>
                              <span className="font-mono">{formatNumber(item.forecast_total_consumption)} {item.base_unit}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-zinc-400">Safety factor:</span>
                              <span className="font-mono">{item.safety_factor}x</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-zinc-400">Target stock:</span>
                              <span className="font-mono">{formatNumber(item.target_stock)} {item.base_unit}</span>
                            </div>
                          </div>
                        </div>

                        {/* Data Quality */}
                        <div>
                          <h4 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Data Quality</h4>
                          <div className="space-y-1 text-sm">
                            <div className="flex items-center gap-2">
                              {item.data_quality.has_recent_consumption_data ? (
                                <CheckCircle className="w-4 h-4 text-emerald-500" />
                              ) : (
                                <XCircle className="w-4 h-4 text-red-500" />
                              )}
                              <span className="text-zinc-400">Consumption data</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {item.data_quality.has_vendor_price ? (
                                <CheckCircle className="w-4 h-4 text-emerald-500" />
                              ) : (
                                <XCircle className="w-4 h-4 text-amber-500" />
                              )}
                              <span className="text-zinc-400">Vendor pricing</span>
                            </div>
                            {item.data_quality.used_sparse_data_logic && (
                              <div className="flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4 text-amber-500" />
                                <span className="text-amber-500 text-xs">Sparse data - lower confidence</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Explanation */}
                      <div className="mt-4 p-3 bg-[#18181b] rounded-lg">
                        <p className="text-sm text-zinc-300">
                          <span className="text-zinc-500 font-medium">Explanation: </span>
                          {item.explanation}
                        </p>
                      </div>

                      {/* Sales Demand Signal — visible only when sales-blended */}
                      {item.demand_source === 'sales_capture' && (
                        <div
                          className="mt-3 p-3 rounded-lg border border-cyan-500/30 bg-cyan-500/5"
                          data-testid="sales-demand-detail-block"
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <Zap className="w-4 h-4 text-cyan-400" />
                            <span className="text-xs font-semibold text-cyan-300 uppercase tracking-wider">
                              Sales Demand Signal
                            </span>
                            <span className="ml-auto text-[10px] uppercase px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-300">
                              {item.sales_confidence || 'medium'} confidence
                            </span>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1 text-xs">
                            <div className="flex justify-between">
                              <span className="text-zinc-500">Pre-sales forecast:</span>
                              <span className="font-mono text-zinc-300">
                                {formatNumber(item.pre_sales_forecast_daily ?? item.forecast_daily_consumption)} {item.base_unit}/day
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-zinc-500">Sales demand:</span>
                              <span className="font-mono text-zinc-300">
                                {formatNumber(item.sales_forecast_qty ?? 0)} {item.base_unit}/day
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-zinc-500">Bucket:</span>
                              <span className="font-mono text-zinc-300">{item.sales_bucket || '—'}</span>
                            </div>
                            {item.confidence_multiplier != null && (
                              <div className="flex justify-between">
                                <span className="text-zinc-500">Conf. multiplier:</span>
                                <span className="font-mono text-zinc-300">{item.confidence_multiplier.toFixed(2)}×</span>
                              </div>
                            )}
                            {item.variance_buffer_multiplier != null && (
                              <div className="flex justify-between">
                                <span className="text-zinc-500">Variance buffer:</span>
                                <span className="font-mono text-zinc-300">{item.variance_buffer_multiplier.toFixed(2)}×</span>
                              </div>
                            )}
                            <div className="flex justify-between">
                              <span className="text-zinc-500">Demand-adjusted:</span>
                              <span className="font-mono text-cyan-300">
                                {formatNumber(item.demand_adjusted_forecast_daily ?? 0)} {item.base_unit}/day
                              </span>
                            </div>
                            <div className="flex justify-between md:col-span-2">
                              <span className="text-zinc-500">Final blended forecast:</span>
                              <span className="font-mono text-emerald-400 font-semibold">
                                {formatNumber(item.forecast_daily_consumption)} {item.base_unit}/day
                              </span>
                            </div>
                          </div>
                          {item.sales_risk_flags && item.sales_risk_flags.length > 0 && (
                            <div className="mt-2 flex items-center gap-1 flex-wrap">
                              <span className="text-[10px] uppercase text-zinc-500">Risk flags:</span>
                              {item.sales_risk_flags.map((rf) => (
                                <span
                                  key={rf}
                                  className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300"
                                >
                                  {rf}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Change vs Yesterday */}
                      {item.change_vs_previous?.has_previous && (
                        <div className={cn(
                          "mt-3 p-3 rounded-lg border",
                          Math.abs(item.change_vs_previous.delta_pct) >= 10
                            ? "bg-amber-500/5 border-amber-500/20"
                            : "bg-zinc-800/50 border-zinc-700"
                        )}>
                          <div className="flex items-center gap-2 mb-2">
                            <TrendingUp className={cn(
                              "w-4 h-4",
                              item.change_vs_previous.delta_qty > 0 ? "text-amber-400" : "text-cyan-400"
                            )} />
                            <span className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">
                              Change vs Yesterday
                            </span>
                          </div>
                          <div className="flex items-baseline gap-4 mb-2">
                            <div>
                              <span className="text-zinc-400 text-xs">Previous: </span>
                              <span className="font-mono text-sm">{item.change_vs_previous.previous_recommended_qty} {item.base_unit}</span>
                            </div>
                            <div>
                              <span className="text-zinc-400 text-xs">Now: </span>
                              <span className="font-mono text-sm font-semibold">{item.change_vs_previous.current_recommended_qty} {item.base_unit}</span>
                            </div>
                            <div className={cn(
                              "font-mono text-sm font-bold",
                              item.change_vs_previous.delta_qty > 0 ? "text-amber-400" : "text-cyan-400"
                            )}>
                              ({item.change_vs_previous.delta_qty > 0 ? '+' : ''}{item.change_vs_previous.delta_qty.toFixed(1)} / {item.change_vs_previous.delta_pct > 0 ? '+' : ''}{item.change_vs_previous.delta_pct.toFixed(0)}%)
                            </div>
                          </div>
                          <p className="text-sm text-zinc-300">
                            {item.change_vs_previous.explanation}
                          </p>
                          {item.change_vs_previous.reason_codes?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {item.change_vs_previous.reason_codes.map((code, idx) => (
                                <span 
                                  key={`crc-${code}-${idx}`}
                                  className="px-2 py-0.5 bg-zinc-700 text-zinc-300 rounded text-xs"
                                >
                                  {code.replace(/_/g, ' ')}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Meta Info */}
          {plan && (
            <div className="text-xs text-zinc-500 text-center">
              Generated at {new Date(plan.generated_at).toLocaleString()} • 
              Method: {plan.meta.forecast_method} • 
              Horizon: {plan.planning_horizon_days} days
              {plan.diagnostics && (
                <> • {plan.diagnostics.items_evaluated} items evaluated, {plan.diagnostics.items_recommended} recommended</>
              )}
            </div>
          )}
        </>
      )}

      {/* Orders View */}
      {view === 'orders' && (
        <div className="space-y-6">
          {/* Draft POs Section */}
          <div>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-500" />
              Draft Orders
              {draftPOs.length > 0 && (
                <span className="text-sm font-normal text-zinc-400">({draftPOs.length})</span>
              )}
            </h2>

            {loadingPOs ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
              </div>
            ) : draftPOs.length === 0 ? (
              <div className="text-center py-12 bg-[#18181b] border border-[#27272a] rounded-lg">
                <FileText className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                <p className="text-zinc-400">No draft purchase orders</p>
                <p className="text-sm text-zinc-500 mt-1">
                  Generate a PO from the Plan view to get started
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {draftPOs.map((po) => (
                  <div
                    key={po.po_id}
                    className="bg-[#18181b] border border-[#27272a] rounded-lg overflow-hidden"
                    data-testid="po-card"
                  >
                    <div className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          {/* Status Badge */}
                          <span className={cn(
                            "px-2 py-1 rounded text-xs font-bold border",
                            getStatusColor(po.status)
                          )}>
                            {po.status}
                          </span>

                          {/* PO Info */}
                          <div>
                            <h3 className="font-bold text-lg flex items-center gap-2" data-testid="po-id">
                              {po.po_id}
                              {!po.vendor_id && (
                                <span className="text-xs text-amber-500 bg-amber-500/10 px-2 py-0.5 rounded">
                                  Manual Selection Required
                                </span>
                              )}
                            </h3>
                            <div className="flex items-center gap-4 text-sm text-zinc-400">
                              <span className="flex items-center gap-1">
                                <Building2 className="w-3 h-3" />
                                {po.vendor_name || 'No Vendor'}
                              </span>
                              <span>•</span>
                              <span>{po.item_count} items</span>
                              <span>•</span>
                              <span className="text-emerald-500 font-semibold">
                                {formatCurrency(po.total_amount)}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleCancelPO(po.po_id)}
                            className="p-2 hover:bg-zinc-700 rounded-lg transition-colors text-zinc-400 hover:text-red-500"
                            title="Cancel PO"
                          >
                            <X className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleApprovePO(po.po_id)}
                            disabled={approvingPO === po.po_id}
                            className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-2 px-4 rounded-lg transition-colors disabled:opacity-50"
                            data-testid="approve-po-btn"
                          >
                            {approvingPO === po.po_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Check className="w-4 h-4" />
                            )}
                            Approve
                          </button>
                          <button
                            onClick={() => toggleExpandPO(po.po_id)}
                            className="p-2 hover:bg-zinc-700 rounded-lg transition-colors"
                          >
                            {expandedPOs[po.po_id] ? (
                              <ChevronUp className="w-5 h-5 text-zinc-400" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-zinc-400" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Line Items */}
                    {expandedPOs[po.po_id] && (
                      <div className="border-t border-[#27272a] bg-[#09090b]">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-[#27272a]">
                              <th className="text-left py-2 px-4 text-zinc-400 font-medium">Item</th>
                              <th className="text-right py-2 px-4 text-zinc-400 font-medium">Qty</th>
                              <th className="text-right py-2 px-4 text-zinc-400 font-medium">Unit Price</th>
                              <th className="text-right py-2 px-4 text-zinc-400 font-medium">Total</th>
                              <th className="text-center py-2 px-4 text-zinc-400 font-medium">Urgency</th>
                            </tr>
                          </thead>
                          <tbody>
                            {po.line_items.map((item, idx) => (
                              <tr key={item.ingredient_id || `po-li-${idx}`} className="border-b border-[#27272a] last:border-0">
                                <td className="py-3 px-4">
                                  <div className="font-medium">{item.ingredient_name}</div>
                                  <div className="text-xs text-zinc-500">{item.base_unit}</div>
                                </td>
                                <td className="text-right py-3 px-4 font-mono">
                                  {formatNumber(item.quantity)}
                                </td>
                                <td className="text-right py-3 px-4 font-mono text-zinc-400">
                                  {formatCurrency(item.unit_price)}
                                </td>
                                <td className="text-right py-3 px-4 font-mono font-semibold">
                                  {formatCurrency(item.line_total)}
                                </td>
                                <td className="text-center py-3 px-4">
                                  <span className={cn(
                                    "px-2 py-0.5 rounded text-xs font-bold",
                                    getUrgencyColor(item.urgency)
                                  )}>
                                    {item.urgency}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="bg-[#18181b]">
                              <td colSpan={3} className="py-3 px-4 text-right font-semibold">
                                Total:
                              </td>
                              <td className="py-3 px-4 text-right font-mono font-bold text-emerald-500">
                                {formatCurrency(po.total_amount)}
                              </td>
                              <td></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Approved POs Section */}
          {approvedPOs.length > 0 && (
            <div>
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500" />
                Approved Orders
                <span className="text-sm font-normal text-zinc-400">({approvedPOs.length})</span>
              </h2>

              <div className="space-y-3">
                {approvedPOs.slice(0, 5).map((po) => (
                  <div
                    key={po.po_id}
                    className="bg-[#18181b] border border-[#27272a] rounded-lg p-4"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <span className={cn(
                          "px-2 py-1 rounded text-xs font-bold border",
                          getStatusColor(po.status)
                        )}>
                          {po.status}
                        </span>
                        <div>
                          <h3 className="font-bold">{po.po_id}</h3>
                          <div className="flex items-center gap-4 text-sm text-zinc-400">
                            <span>{po.vendor_name}</span>
                            <span>•</span>
                            <span>{po.item_count} items</span>
                            <span>•</span>
                            <span className="text-emerald-500">{formatCurrency(po.total_amount)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right text-sm text-zinc-500">
                        <p>Approved: {new Date(po.approved_at).toLocaleDateString()}</p>
                        <p>By: {po.approved_by_email}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Demand Signals/Overrides View */}
      {view === 'overrides' && (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Zap className="w-5 h-5 text-violet-500" />
                Demand Signals
              </h2>
              <p className="text-sm text-zinc-400 mt-1">
                Tell the system about upcoming events, holidays, or demand changes
              </p>
            </div>
            <button
              onClick={() => setShowCreateOverride(true)}
              className="flex items-center gap-2 bg-violet-500 hover:bg-violet-600 text-white font-bold py-2 px-4 rounded-lg transition-colors"
              data-testid="add-signal-btn"
            >
              <Plus className="w-5 h-5" />
              Add Signal
            </button>
          </div>

          {/* Create Override Modal */}
          {showCreateOverride && (
            <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-6">
              <h3 className="text-lg font-bold mb-4">Create Demand Signal</h3>
              <form onSubmit={handleCreateOverride} className="space-y-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Event Name *</label>
                  <input
                    type="text"
                    value={newOverride.name}
                    onChange={(e) => setNewOverride(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Goa Long Weekend, Diwali Rush"
                    className="w-full bg-[#09090b] border border-[#27272a] rounded-lg px-4 py-2 focus:outline-none focus:border-violet-500"
                    data-testid="signal-name-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Start Date *</label>
                    <input
                      type="date"
                      value={newOverride.start_date}
                      onChange={(e) => setNewOverride(prev => ({ ...prev, start_date: e.target.value }))}
                      className="w-full bg-[#09090b] border border-[#27272a] rounded-lg px-4 py-2 focus:outline-none focus:border-violet-500"
                      data-testid="signal-start-date"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">End Date *</label>
                    <input
                      type="date"
                      value={newOverride.end_date}
                      onChange={(e) => setNewOverride(prev => ({ ...prev, end_date: e.target.value }))}
                      className="w-full bg-[#09090b] border border-[#27272a] rounded-lg px-4 py-2 focus:outline-none focus:border-violet-500"
                      data-testid="signal-end-date"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">
                    Demand Multiplier: <span className="text-white font-bold">{newOverride.demand_multiplier}x</span>
                  </label>
                  <input
                    type="range"
                    min="0.5"
                    max="3"
                    step="0.1"
                    value={newOverride.demand_multiplier}
                    onChange={(e) => setNewOverride(prev => ({ ...prev, demand_multiplier: parseFloat(e.target.value) }))}
                    className="w-full accent-violet-500"
                  />
                  <div className="flex justify-between text-xs text-zinc-500 mt-1">
                    <span>0.5x (Low demand)</span>
                    <span>1x (Normal)</span>
                    <span>3x (High demand)</span>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Notes (optional)</label>
                  <textarea
                    value={newOverride.notes}
                    onChange={(e) => setNewOverride(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Any additional context..."
                    className="w-full bg-[#09090b] border border-[#27272a] rounded-lg px-4 py-2 focus:outline-none focus:border-violet-500 h-20 resize-none"
                  />
                </div>
                
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateOverride(false)}
                    className="px-4 py-2 text-zinc-400 hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-violet-500 hover:bg-violet-600 text-white font-bold rounded-lg transition-colors"
                    data-testid="create-signal-btn"
                  >
                    Create Signal
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Active Overrides List */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Active & Upcoming Signals</h3>
            
            {loadingOverrides ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
              </div>
            ) : overrides.length === 0 ? (
              <div className="text-center py-12 bg-[#18181b] border border-[#27272a] rounded-lg">
                <Calendar className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                <p className="text-zinc-400">No demand signals configured</p>
                <p className="text-sm text-zinc-500 mt-1">
                  Add signals for events, holidays, or expected demand changes
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {overrides.map((override) => {
                  const startDate = new Date(override.start_date);
                  const endDate = new Date(override.end_date);
                  const now = new Date();
                  const isActive = now >= startDate && now <= endDate;
                  const isPast = now > endDate;
                  
                  return (
                    <div
                      key={override.override_id}
                      className={cn(
                        "bg-[#18181b] border rounded-lg p-4",
                        isActive ? "border-violet-500/50" : "border-[#27272a]"
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          {isActive && (
                            <span className="px-2 py-1 bg-violet-500/10 text-violet-400 border border-violet-500/30 rounded text-xs font-bold animate-pulse">
                              ACTIVE
                            </span>
                          )}
                          {isPast && (
                            <span className="px-2 py-1 bg-zinc-500/10 text-zinc-400 border border-zinc-500/30 rounded text-xs font-bold">
                              ENDED
                            </span>
                          )}
                          {!isActive && !isPast && (
                            <span className="px-2 py-1 bg-blue-500/10 text-blue-400 border border-blue-500/30 rounded text-xs font-bold">
                              UPCOMING
                            </span>
                          )}
                          
                          <div>
                            <h4 className="font-bold text-lg">{override.name}</h4>
                            <div className="flex items-center gap-4 text-sm text-zinc-400">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {startDate.toLocaleDateString()} - {endDate.toLocaleDateString()}
                              </span>
                              <span className={cn(
                                "font-semibold",
                                override.demand_multiplier > 1 ? "text-amber-500" : "text-blue-500"
                              )}>
                                {override.demand_multiplier}x demand
                              </span>
                            </div>
                            {override.notes && (
                              <p className="text-xs text-zinc-500 mt-1">{override.notes}</p>
                            )}
                          </div>
                        </div>
                        
                        <button
                          onClick={() => handleDeleteOverride(override.override_id)}
                          className="p-2 hover:bg-zinc-700 rounded-lg transition-colors text-zinc-400 hover:text-red-500"
                          title="Delete signal"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Info Box */}
          <div className="bg-violet-500/5 border border-violet-500/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-violet-400 mt-0.5" />
              <div>
                <p className="text-sm text-violet-200">
                  <strong>How signals work:</strong> When a signal is active, the procurement engine multiplies 
                  the forecasted demand by the specified multiplier. This ensures you stock up before 
                  high-demand periods and reduce orders during expected slow periods.
                </p>
                <p className="text-xs text-zinc-500 mt-2">
                  Example: A 1.5x multiplier during a long weekend will increase all procurement recommendations by 50%.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Forecast Accuracy View */}
      {view === 'accuracy' && (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Target className="w-5 h-5 text-cyan-500" />
                Forecast Accuracy
              </h2>
              <p className="text-sm text-zinc-400 mt-1">
                How accurate have the procurement recommendations been?
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-zinc-400">Period:</span>
              <select
                value={accuracyPeriod}
                onChange={(e) => setAccuracyPeriod(parseInt(e.target.value))}
                className="bg-[#18181b] border border-[#27272a] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-cyan-500"
              >
                <option value={7}>Last 7 days</option>
                <option value={14}>Last 14 days</option>
                <option value={30}>Last 30 days</option>
              </select>
            </div>
          </div>

          {loadingAccuracy ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
            </div>
          ) : !accuracy || accuracy.total_ingredients === 0 ? (
            <div className="text-center py-12 bg-[#18181b] border border-[#27272a] rounded-lg">
              <BarChart3 className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400">No forecast data available yet</p>
              <p className="text-sm text-zinc-500 mt-1">
                Forecasts are stored when you generate procurement plans.
                Check back after a few days of usage.
              </p>
            </div>
          ) : (
            <>
              {/* Overall Accuracy Card */}
              <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-zinc-400 uppercase tracking-wider mb-1">Overall Accuracy</p>
                    <p className={cn(
                      "text-4xl font-bold",
                      accuracy.overall_accuracy_rate >= 0.7 ? "text-emerald-500" :
                      accuracy.overall_accuracy_rate >= 0.5 ? "text-amber-500" : "text-red-500"
                    )}>
                      {(accuracy.overall_accuracy_rate * 100).toFixed(0)}%
                    </p>
                    <p className="text-sm text-zinc-500 mt-1">
                      {accuracy.total_forecasts_with_actuals} forecasts with actual data
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-zinc-400 uppercase tracking-wider mb-1">Ingredients Tracked</p>
                    <p className="text-2xl font-bold">{accuracy.total_ingredients}</p>
                    <p className="text-sm text-zinc-500 mt-1">
                      Over {accuracy.period_days} days
                    </p>
                  </div>
                </div>
              </div>

              {/* Worst & Best Performing */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Needs Attention */}
                <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-amber-500 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Needs Attention
                  </h3>
                  {accuracy.worst_performing && accuracy.worst_performing.length > 0 ? (
                    <div className="space-y-2">
                      {accuracy.worst_performing.map((ing, idx) => (
                        <div key={ing.ingredient_id} className="flex items-center justify-between p-2 bg-[#09090b] rounded">
                          <span className="text-sm">{ing.ingredient_name}</span>
                          <span className={cn(
                            "text-sm font-mono",
                            ing.accuracy_rate < 0.5 ? "text-red-500" : "text-amber-500"
                          )}>
                            {(ing.accuracy_rate * 100).toFixed(0)}% accurate
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-zinc-500">No data yet</p>
                  )}
                </div>

                {/* Top Performers */}
                <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-emerald-500 mb-3 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Top Performers
                  </h3>
                  {accuracy.best_performing && accuracy.best_performing.length > 0 ? (
                    <div className="space-y-2">
                      {accuracy.best_performing.map((ing, idx) => (
                        <div key={ing.ingredient_id} className="flex items-center justify-between p-2 bg-[#09090b] rounded">
                          <span className="text-sm">{ing.ingredient_name}</span>
                          <span className="text-sm font-mono text-emerald-500">
                            {(ing.accuracy_rate * 100).toFixed(0)}% accurate
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-zinc-500">No data yet</p>
                  )}
                </div>
              </div>

              {/* All Ingredients Table */}
              <div className="bg-[#18181b] border border-[#27272a] rounded-lg overflow-hidden">
                <div className="p-4 border-b border-[#27272a]">
                  <h3 className="font-semibold">All Ingredients</h3>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#27272a] bg-[#09090b]">
                      <th className="text-left py-3 px-4 text-zinc-400 font-medium">Ingredient</th>
                      <th className="text-right py-3 px-4 text-zinc-400 font-medium">Forecasts</th>
                      <th className="text-right py-3 px-4 text-zinc-400 font-medium">Accurate</th>
                      <th className="text-right py-3 px-4 text-zinc-400 font-medium">Accuracy</th>
                      <th className="text-right py-3 px-4 text-zinc-400 font-medium">Avg Error</th>
                      <th className="text-right py-3 px-4 text-zinc-400 font-medium">Confidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accuracy.ingredients.map((ing) => (
                      <tr key={ing.ingredient_id} className="border-b border-[#27272a] last:border-0 hover:bg-[#1f1f23]">
                        <td className="py-3 px-4 font-medium">{ing.ingredient_name}</td>
                        <td className="text-right py-3 px-4 font-mono text-zinc-400">
                          {ing.forecasts_with_actuals}
                        </td>
                        <td className="text-right py-3 px-4 font-mono text-zinc-400">
                          {ing.accurate_forecasts}
                        </td>
                        <td className="text-right py-3 px-4">
                          <span className={cn(
                            "font-mono font-semibold",
                            ing.accuracy_rate >= 0.7 ? "text-emerald-500" :
                            ing.accuracy_rate >= 0.5 ? "text-amber-500" : "text-red-500"
                          )}>
                            {(ing.accuracy_rate * 100).toFixed(0)}%
                          </span>
                        </td>
                        <td className="text-right py-3 px-4 font-mono text-zinc-400">
                          {(ing.avg_error_pct * 100).toFixed(0)}%
                        </td>
                        <td className="text-right py-3 px-4 font-mono text-zinc-400">
                          {(ing.avg_confidence * 100).toFixed(0)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Info Box */}
              <div className="bg-cyan-500/5 border border-cyan-500/20 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-cyan-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-cyan-200">
                      <strong>How accuracy is measured:</strong> A forecast is "accurate" if the actual consumption 
                      was within 20% of the predicted amount. High accuracy (&gt;70%) means the system's recommendations 
                      are reliable for that ingredient.
                    </p>
                    <p className="text-xs text-zinc-500 mt-2">
                      Ingredients with low accuracy may have irregular demand patterns or insufficient historical data.
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Procurement;
